import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Check, X, Minus } from "lucide-react";
import heroImage from "@/assets/gallery/aerial-river-cabanas.jpg";

const competitors = [
  { key: "sons", label: "Son's Guadalupe", highlight: true },
  { key: "park", label: "Guadalupe River State Park" },
  { key: "rockin", label: "Rockin' R River Rides" },
  { key: "fimfo", label: "Camp Fimfo Texas Hill Country" },
];

type Cell = { v: string; status?: "yes" | "no" | "neutral" };

const rows: { feature: string; cells: Record<string, Cell> }[] = [
  {
    feature: "Walk from parking to river",
    cells: {
      sons: { v: "0–50 ft (right at your cabana)", status: "yes" },
      park: { v: "200–500 ft to entry points", status: "neutral" },
      rockin: { v: "Shuttle ride + hike to entry", status: "no" },
      fimfo: { v: "100–300 ft from RV/cabin", status: "neutral" },
    },
  },
  {
    feature: "Float ends at your private space",
    cells: {
      sons: { v: "Yes — float ends at your cabana", status: "yes" },
      park: { v: "No — public takeout", status: "no" },
      rockin: { v: "No — shuttle pickup at exit", status: "no" },
      fimfo: { v: "No — public takeout", status: "no" },
    },
  },
  {
    feature: "Private shaded basecamp",
    cells: {
      sons: { v: "22 private riverside cabanas", status: "yes" },
      park: { v: "First-come picnic shelters", status: "no" },
      rockin: { v: "None — outfitter only", status: "no" },
      fimfo: { v: "RV/cabin lots", status: "neutral" },
    },
  },
  {
    feature: "In-water covered picnic tables",
    cells: {
      sons: { v: "Yes — sit right in the river", status: "yes" },
      park: { v: "No", status: "no" },
      rockin: { v: "No", status: "no" },
      fimfo: { v: "No", status: "no" },
    },
  },
  {
    feature: "Private BBQ grill at your spot",
    cells: {
      sons: { v: "Yes — every cabana", status: "yes" },
      park: { v: "Shared public grills", status: "neutral" },
      rockin: { v: "No", status: "no" },
      fimfo: { v: "Some sites", status: "neutral" },
    },
  },
  {
    feature: "Daily capacity",
    cells: {
      sons: { v: "Limited — never crowded", status: "yes" },
      park: { v: "Often hits capacity by 11 AM summer weekends", status: "no" },
      rockin: { v: "High-volume shuttles", status: "no" },
      fimfo: { v: "Full RV resort", status: "neutral" },
    },
  },
  {
    feature: "Onsite restrooms & private showers",
    cells: {
      sons: { v: "Yes", status: "yes" },
      park: { v: "Public restrooms only", status: "neutral" },
      rockin: { v: "Outfitter restrooms", status: "neutral" },
      fimfo: { v: "Yes", status: "yes" },
    },
  },
  {
    feature: "Tubing included / available",
    cells: {
      sons: { v: "Shuttle-assisted tubing $24.99", status: "yes" },
      park: { v: "BYO — no rentals onsite", status: "neutral" },
      rockin: { v: "Yes — primary service", status: "yes" },
      fimfo: { v: "BYO or partner outfitter", status: "neutral" },
    },
  },
  {
    feature: "Day-use price (starting)",
    cells: {
      sons: { v: "Cabana from $69 / picnic table from less", status: "yes" },
      park: { v: "$7/adult day-use entry", status: "yes" },
      rockin: { v: "~$25/person tube + parking fees", status: "neutral" },
      fimfo: { v: "Overnight only", status: "no" },
    },
  },
];

const StatusIcon = ({ status }: { status?: Cell["status"] }) => {
  if (status === "yes") return <Check className="w-4 h-4 text-teal flex-shrink-0" />;
  if (status === "no") return <X className="w-4 h-4 text-red-500 flex-shrink-0" />;
  return <Minus className="w-4 h-4 text-charcoal/70 flex-shrink-0" aria-label="Not available" />;
};

const PrivateVsPublicGuadalupe = () => {
  return (
    <div className="min-h-screen">
      <SEOHead
        title="Private Cabanas vs. Public River Spots on the Guadalupe"
        description="Compare Son's Guadalupe private cabanas with Guadalupe River State Park, Rockin' R, and Camp Fimfo: parking, walks, shuttles, crowds, BBQ & price."
        canonical="/private-vs-public-guadalupe"
        ogImage="/og/home.jpg"
      />
      <Header />
      <main className="pt-24">
        <section className="relative h-[50vh] min-h-[380px]">
          <img fetchPriority="high" loading="eager" decoding="async" src={heroImage} alt="Aerial view of private riverside cabanas vs crowded public Guadalupe River access" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/80 via-charcoal/50 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="container-narrow mx-auto px-4 md:px-8">
              <div className="max-w-3xl">
                <p className="text-coral-orange font-medium mb-3">Honest comparison</p>
                <h1 className="font-heading text-4xl md:text-6xl font-bold text-white mb-4">
                  Private Cabanas vs. Public River Spots on the Guadalupe
                </h1>
                <p className="text-white/90 text-lg md:text-xl max-w-2xl">
                  Why families pay for a private cabana over a free public park spot — parking distance, walks, shuttles, crowds, and what you actually get.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="section-padding bg-background">
          <div className="container-narrow mx-auto max-w-3xl px-4 md:px-8">
            <p className="text-muted-foreground text-lg leading-relaxed mb-4">
              The Guadalupe River near New Braunfels has dozens of public access points and a handful of high-volume tubing outfitters. They're great when they work — and miserable on a hot Saturday in July when every parking lot is full, every shuttle is delayed, and you're walking half a mile back to the car with a kid on your hip.
            </p>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Son's Guadalupe is the private alternative: 22 riverside cabanas where the float literally ends at your shaded home base. No shuttle. No long walk. No parking-lot chaos. Below is an honest, side-by-side look at how that compares to the popular public and outfitter options on the same stretch of river.
            </p>
          </div>
        </section>

        <section className="section-padding bg-cream">
          <div className="container-narrow mx-auto px-4 md:px-8">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-charcoal mb-8 text-center">
              Side-by-side comparison
            </h2>
            <div className="overflow-x-auto bg-white rounded-2xl shadow-soft">
              <table className="w-full text-sm md:text-base">
                <thead>
                  <tr className="bg-teal text-white">
                    <th className="text-left p-4 font-heading">Feature</th>
                    {competitors.map((c) => (
                      <th key={c.key} className={`text-left p-4 font-heading min-w-[180px] ${c.highlight ? "bg-coral-orange" : ""}`}>
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.feature} className="border-t border-cream">
                      <td className="p-4 font-semibold text-charcoal align-top">{row.feature}</td>
                      {competitors.map((c) => {
                        const cell = row.cells[c.key];
                        return (
                          <td key={c.key} className={`p-4 align-top ${c.highlight ? "bg-cream/40" : ""}`}>
                            <div className="flex items-start gap-2">
                              <StatusIcon status={cell.status} />
                              <span className="text-charcoal/80">{cell.v}</span>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-charcoal/60 text-sm text-center mt-4">
              Comparison facts based on publicly listed amenities and operator information as of 2026. Prices change seasonally — always check each operator's site.
            </p>
          </div>
        </section>

        <section className="section-padding bg-teal text-white">
          <div className="container-narrow mx-auto px-4 md:px-8 text-center max-w-2xl">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">Skip the crowds. Float to your own cabana.</h2>
            <p className="text-white/85 text-lg mb-6">Cabanas from $69, glamping from $99, and 25% off tubing when you book a cabana.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book-now"><Button variant="hero" size="xl">Book Your Cabana</Button></Link>
              <Link to="/cabanas"><Button variant="hero-outline" size="xl">See What's Included</Button></Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default PrivateVsPublicGuadalupe;
